import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GenerateConsole } from '../../components/GenerateConsole';
import { NoSpecSelected } from '../../components/NoSpecSelected';
import {
  StrategySelector,
  StrategySelectorControls,
  StrategySelectorHeader,
  StrategySelectorList,
  StrategySelectorTitle,
} from '../../components/StrategySelector';
import { useSpec } from '../../contexts/SpecContext';
import { useGenerationSocket } from '../../hooks/useGenerationSocket';
import { HypothesisDetailModal } from './HypothesisDetailModal';
import { useActiveStrategies } from './hooks/useActiveStrategies';
import styles from './TestHypotheses.module.css';

const API_URL = import.meta.env.VITE_API_URL || '';

const VALID_SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'];

const normalizeSeverity = (severity: string | undefined): string => {
  const normalized = severity?.toLowerCase();
  return normalized && VALID_SEVERITIES.includes(normalized) ? normalized : 'Unknown';
};

interface TestHypothesesProps {
  onOpenRightDrawer?: (content: { title: string; component: ReactNode }) => void;
}

export const TestHypotheses: FC<TestHypothesesProps> = ({ onOpenRightDrawer: _onOpenRightDrawer }) => {
  const { hasActiveSpec, activeSpec } = useSpec();
  const navigate = useNavigate();
  const [hypotheses, setHypotheses] = useState<any[]>([]);
  const [testFilenames, setTestFilenames] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedHypothesis, setSelectedHypothesis] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const {
    strategies: activeStrategies,
    selectedStrategies,
    selectedSet,
    loading: strategiesLoading,
    toggleStrategy,
    selectAll,
    deselectAll,
  } = useActiveStrategies();

  const loadHypotheses = useCallback(async () => {
    setLoading(true);
    try {
      const [hypothesesRes, testsRes] = await Promise.all([
        fetch(`${API_URL}/api/hypotheses/${activeSpec.id}`),
        fetch(`${API_URL}/api/tests/${activeSpec.id}`),
      ]);

      if (hypothesesRes.ok) {
        const data = await hypothesesRes.json();
        if (data.success) {
          const validHypotheses = (data.hypotheses || [])
            .filter((h: any) => h.title?.trim())
            .map((h: any) => ({
              ...h,
              severity: normalizeSeverity(h.severity),
            }));
          setHypotheses(validHypotheses);
        }
      }

      if (testsRes.ok) {
        const data = await testsRes.json();
        if (data.success) {
          setTestFilenames((data.tests || []).map((t: any) => t.filename as string));
        }
      }
    } catch (error) {
      console.error('Error loading hypotheses:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSpec?.id]);

  useEffect(() => {
    if (hasActiveSpec && activeSpec) {
      loadHypotheses();
    }
  }, [hasActiveSpec, activeSpec, loadHypotheses]);

  const { generating, consoleOutput, generationStatus, startGeneration, failGeneration, stopGeneration } =
    useGenerationSocket({
      eventPrefix: 'hypothesis-generation',
      schemaId: activeSpec?.id,
      onComplete: loadHypotheses,
    });

  const handleCardClick = (hypothesis: any) => {
    if (activeSpec?.id && hypothesis.filename) {
      navigate(`/hypotheses/${activeSpec.id}/${hypothesis.filename}`);
    } else {
      setSelectedHypothesis(hypothesis);
      setModalOpen(true);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedHypothesis(null);
  };

  const handleGenerateHypotheses = async () => {
    if (!activeSpec) return;

    startGeneration('Starting hypothesis generation...\n');

    try {
      const response = await fetch(`${API_URL}/api/generate/hypotheses/${activeSpec.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activeStrategies: selectedStrategies }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        failGeneration(`Error: ${data.message || 'Failed to start generation'}`);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error starting hypothesis generation:', error);
      failGeneration(`Error: ${message}`);
    }
  };

  const handleStopGeneration = async () => {
    if (!activeSpec) return;

    try {
      await fetch(`${API_URL}/api/generate/hypotheses/${activeSpec.id}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      stopGeneration();
    } catch (error) {
      console.error('Error stopping hypothesis generation:', error);
    }
  };

  if (!hasActiveSpec) {
    return <NoSpecSelected />;
  }

  if (loading || strategiesLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <h3>Loading test hypotheses...</h3>
        </div>
      </div>
    );
  }

  const getTestCount = (hypothesis: any): number => {
    const baseName = hypothesis.filename?.replace('.json', '');
    return baseName ? testFilenames.filter((t) => t.startsWith(baseName)).length : 0;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Test Hypotheses</h1>
        <p className={styles.description}>
          Test Hypotheses generates AI-powered security test cases for your API using active testing strategies. Select
          which strategies to include, then click Generate to create hypothesis files. Active strategies send modified
          requests to test for vulnerabilities like BOLA, SQL injection, etc.
        </p>
      </div>

      <StrategySelector strategies={activeStrategies} selectedKeys={selectedSet} onToggle={toggleStrategy}>
        <StrategySelectorHeader>
          <StrategySelectorTitle>Active Strategies</StrategySelectorTitle>
          <StrategySelectorControls onSelectAll={selectAll} onDeselectAll={deselectAll} />
        </StrategySelectorHeader>
        <StrategySelectorList emptyMessage="No active strategies available." />
      </StrategySelector>

      <GenerateConsole
        generating={generating}
        consoleOutput={consoleOutput}
        generationStatus={generationStatus}
        disabled={!activeSpec || selectedStrategies.length === 0}
        title="Generate Hypotheses"
        description={
          selectedStrategies.length > 0
            ? `for ${selectedStrategies.length} selected ${selectedStrategies.length === 1 ? 'strategy' : 'strategies'}`
            : undefined
        }
        runLabel="Generate Test Hypotheses"
        stopLabel="Stop Generation"
        onGenerate={handleGenerateHypotheses}
        onStop={handleStopGeneration}
      />

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            Generated Hypotheses
            {hypotheses.length > 0 && <span className={styles.badge}>{hypotheses.length}</span>}
          </h2>
        </div>

        {hypotheses.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No Hypotheses Found</h3>
            <p>
              No test hypotheses have been generated for this specification yet. Generate hypotheses using the AI test
              generation tools.
            </p>
          </div>
        ) : (
          <div className={styles.hypothesesList}>
            {hypotheses.map((hypothesis, index) => (
              <div key={index} className={styles.hypothesisCard} onClick={() => handleCardClick(hypothesis)}>
                <span className={styles.categoryBadge}>{hypothesis.category}</span>
                <span className={styles.severityBadge} data-severity={hypothesis.severity?.toLowerCase()}>
                  {hypothesis.severity}
                </span>
                {(() => {
                  const count = getTestCount(hypothesis);
                  return count > 0 ? (
                    <span className={styles.testCountBadge}>
                      {count} test{count !== 1 ? 's' : ''}
                    </span>
                  ) : null;
                })()}
                <h3 className={styles.cardTitle}>{hypothesis.title}</h3>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && selectedHypothesis && (
        <HypothesisDetailModal hypothesis={selectedHypothesis} onClose={closeModal} />
      )}
    </div>
  );
};
